import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Flame, Gem, Hourglass, Loader2, Map, ShoppingBag, ScrollText, Snowflake, Trophy, ShieldAlert, Users, Star, Wallet, X } from 'lucide-react';

import { gbApi, gbFmt, gbError } from '../components/gb/gbApi';
import { gbAviso, GbNotices } from '../components/gb/GbNotice';
import { GbWorldMap } from '../components/gb/GbWorldMap';
import { GbShop } from '../components/gb/GbShop';
import { GbBattle } from '../components/gb/GbBattle';
import { GbRanking } from '../components/gb/GbRanking';
import { GbClans } from '../components/gb/GbClans';
import { GbPvp } from '../components/gb/GbPvp';
import { GbWallet } from '../components/gb/GbWallet';
import { GbStatement } from '../components/gb/GbStatement';
import { GbCoin } from '../components/gb/GbCoin';
import { GbGems } from '../components/gb/GbGems';
import { GbClanBadge } from '../components/gb/GbClanBadge';
import { GbLoading } from '../components/gb/GbLoading';
import { GbAudioMenu } from '../components/gb/GbAudioMenu';
import { GbClanInvite } from '../components/gb/GbClanInvite';
import { gbMusic, gbPreloadAudio, gbWarmSfx } from '../components/gb/gbSfx';
import { preloadGb } from '../components/gb/gbPreload';
import { useGbScreen } from '../components/gb/GbScreen';

const TABS = [
  { id: 'missoes', label: 'MISSÕES', Icon: Map },
  { id: 'loja', label: 'LOJA', Icon: ShoppingBag },
  { id: 'carteira', label: 'CARTEIRA', Icon: Wallet },
  { id: 'extrato', label: 'EXTRATO', Icon: ScrollText },
  { id: 'ranking', label: 'RANKING', Icon: Trophy },
  { id: 'clas', label: 'CLÃS', Icon: Users },
];

// quanto falta para o evento acabar, em palavra curta (igual ao Oil Tycoon)
const contagem = (s) => {
  s = Math.max(0, Math.floor(Number(s) || 0));
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600),
    m = Math.floor((s % 3600) / 60), seg = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${seg}s`;
  return `${m}m ${seg}s`;
};

// brasas subindo na capa: metade laranja (fogo), metade azul (gelo)
const BRASAS = Array.from({ length: 12 }, (_, i) => ({
  left: `${4 + i * 8 + (i % 3) * 2}%`,
  cor: i % 2 ? 'rgba(96,205,255,.85)' : 'rgba(255,146,40,.9)',
  dur: 7 + (i % 5) * 1.8,
  atraso: (i % 6) * 1.4,
}));

export default function GbArenaPage() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('inicio');
  const [starting, setStarting] = useState(false);
  const [battle, setBattle] = useState(null);
  const [avatar, setAvatar] = useState('');
  const [recem, setRecem] = useState(0);
  const [pronto, setPronto] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [pvpFase, setPvpFase] = useState('idle');
  const [convite, setConvite] = useState(null);         // convite de clã aberto por link
  const [entrandoCla, setEntrandoCla] = useState(false);
  const [segundos, setSegundos] = useState(0);          // cronômetro regressivo do evento
  const tela = useGbScreen();

  useEffect(() => {          // baixa mapas, tanques e efeitos ANTES de entrar na arena
    let vivo = true;
    // as trilhas de música (~17 MB) NÃO travam mais a entrada: elas chegam em segundo
    // plano depois que a arena abriu. Antes a conexão ao vivo (websocket) só começava
    // depois de baixar tudo, e no celular isso parecia "demorando para conectar".
    preloadGb((p) => { if (vivo) setProgresso(Math.round(p * 0.92)); })
      .then(() => gbWarmSfx())                 // efeitos são leves: já entram prontos
      .finally(() => {
        if (!vivo) return;
        setProgresso(100); setPronto(true);
        gbPreloadAudio();                      // trilhas em segundo plano, sem travar nada
      });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {          // sem arrastar a página nem fundo branco no overscroll
    const h = document.documentElement, b = document.body;
    const antes = [h.style.overscrollBehavior, b.style.overscrollBehavior, b.style.overflow, b.style.background, h.style.background];
    h.style.background = '#0b0712';
    h.style.overscrollBehavior = 'none';
    b.style.overscrollBehavior = 'none';
    b.style.overflow = 'hidden';
    b.style.background = '#0b0712';
    return () => {
      [h.style.overscrollBehavior, b.style.overscrollBehavior, b.style.overflow, b.style.background, h.style.background] = antes;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await gbApi.get('/state');
      setState(data);
      setSegundos(Math.max(0, Math.floor(Number(data?.seconds_left) || 0)));
    } catch (e) {
      if (e?.response?.status === 401) { navigate('/conta'); return; }
      gbAviso.erro(gbError(e));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // convite de clã por link: /arena?cla=codigo — mostra o clã e só entra se o jogador aceitar
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('cla');
    if (!code || !state?.active) return;
    window.history.replaceState({}, '', '/arena');
    (async () => {
      try {
        const { data } = await gbApi.get(`/clans/invite/${encodeURIComponent(code)}`);
        setConvite(data);
      } catch (e) { gbAviso.erro(gbError(e)); }
    })();
  }, [state?.active]);

  const aceitarConvite = async () => {
    if (!convite?.code) return;
    setEntrandoCla(true);
    try {
      const { data } = await gbApi.post('/clans/join', { code: convite.code });
      gbAviso.ok(`Você entrou no clã ${data.clan?.name || ''}!`);
      setConvite(null);
      setTab('clas');
      await load();
    } catch (e) { gbAviso.erro(gbError(e)); } finally { setEntrandoCla(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem('customer_token')) { navigate('/conta'); return; }
    load();
  }, [load, navigate]);

  // cronômetro regressivo do evento: desce de segundo em segundo na tela
  useEffect(() => {
    if (!state?.active) return;
    const t = setInterval(() => setSegundos((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [state?.active]);

  // zerou o prazo: confere uma vez com o servidor se o evento encerrou mesmo
  const conferiuFim = useRef(false);
  useEffect(() => {
    if (!state?.active || segundos > 0 || conferiuFim.current) return;
    conferiuFim.current = true;
    const t = setTimeout(load, 2000);
    return () => clearTimeout(t);
  }, [segundos, state?.active, load]);

  // trilha de fundo de cada tela · na partida a trilha do duelo continua tocando
  // (o mesmo motor de áudio da página: no iPhone o som só sai do documento que o
  // jogador tocou, então quem toca é sempre a página de fora)
  useEffect(() => {
    if (!state?.active) return;
    const naPartida = !!battle || ['match', 'partida', 'assistindo'].includes(pvpFase);
    const porAba = { loja: 'loja', missoes: 'missoes', carteira: 'carteira', diamantes: 'carteira',
      extrato: 'extrato', ranking: 'ranking', clas: 'clas' };
    gbMusic(naPartida ? 'duelo'
      : (pvpFase === 'sala' || pvpFase === 'fila') ? 'sala'
        : (porAba[tab] || 'inicio'));
  }, [tab, battle, pvpFase, state?.active]);

  useEffect(() => () => gbMusic(null), []);     // saiu da arena: silencia a trilha

  // na sala de combinação o jogador não troca de aba: a sala existe só para combinar o par
  const naSala = pvpFase === 'sala' || pvpFase === 'fila';

  useEffect(() => {
    const kakoId = state?.player?.kako_id;
    if (!kakoId) return;
    setAvatar(state.player.avatar || '');
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/kako/verify-user`, { kako_id: kakoId })
      .then(({ data }) => { if (data?.user?.avatar) setAvatar(data.user.avatar); })
      .catch(() => {});
  }, [state?.player?.kako_id, state?.player?.avatar]);

  const play = async (node) => {
    if (state?.pvp_locked) {
      gbAviso.erro(state?.pvp_locked_message || 'Manutenção programada: as fases voltam em breve.');
      return;
    }
    tela.entrar();          // tela cheia (esconde as barras do navegador); joga em pé ou de lado
    setStarting(true);
    try {
      const { data } = await gbApi.post(`/mission/${node.node}/start`);
      setBattle({ match: data, node });
      await load();
    } catch (e) {
      gbAviso.erro(gbError(e));
    } finally {
      setStarting(false);
    }
  };

  // o resultado é mostrado no card dentro do jogo — sem bolhas de notificação do site por cima
  const onFinished = async (res) => {
    await load();
    if (!res) return;
    if (Number(res.best_stars || res.stars || 0) > 0) setRecem(Number(battle?.node?.node || 0));
  };

  if (loading || !pronto) {
    // a barra só chega a 100% quando os arquivos E os dados do evento estão prontos
    return <GbLoading progress={loading ? Math.min(progresso, 96) : progresso} />;
  }

  // evento encerrando: a Arena continua aberta para quem precisa TERMINAR a partida
  const encerrando = !state?.active && !!state?.can_finish_match;
  if (!state?.active && !encerrando) {
    const desativado = state?.event_status === 'desativado';
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0b0712] px-6 text-center"
        data-testid="gb-arena-fechada">
        <img src="/gb/mobile.png" alt="" className="w-40 opacity-70 drop-shadow-[0_0_30px_rgba(255,107,0,.5)]" />
        <h1 className="text-2xl font-black text-orange-300">
          {desativado ? 'ARENA EM MANUTENÇÃO' : 'ARENA FECHADA'}
        </h1>
        <p className="max-w-sm text-sm text-purple-200/70">
          {desativado
            ? 'O evento está desativado por alguns instantes. Seu saldo e seu progresso estão guardados — volte já.'
            : 'Nenhuma temporada da Arena está acontecendo agora. Fique de olho — a próxima vem com novos mapas e prêmios.'}
        </p>
        <button onClick={() => navigate('/conta')} data-testid="gb-closed-back"
          className="mt-2 rounded-full border border-orange-600 px-6 py-2 text-xs font-black text-orange-400">
          VOLTAR
        </button>
      </div>
    );
  }

  const { player, config } = state;

  return (
    <div className="pwa-safe-top fixed inset-0 isolate flex flex-col overflow-hidden bg-[#07040d] text-purple-50"
      data-testid="gb-arena-page">

      {/* arte da arena: retrato no celular, paisagem no computador
          (-z-10: fica atrás de tudo sem criar camada que prenda a batalha) */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden="true">
        <img src="/gb/capa_retrato.jpg" alt="" data-testid="gb-bg-portrait"
          className="h-full w-full object-cover object-[50%_42%] md:hidden" />
        <img src="/gb/capa_paisagem.jpg" alt="" data-testid="gb-bg-landscape"
          className="hidden h-full w-full object-cover object-center md:block" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_34%,transparent_0%,rgba(7,4,13,.5)_52%,rgba(7,4,13,.96)_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07040d]/80 via-[#07040d]/25 to-[#07040d]/95" />
        {BRASAS.map((b, i) => (
          <span key={i} className="gb-brasa"
            style={{ left: b.left, background: b.cor, boxShadow: `0 0 10px ${b.cor}`,
              animationDuration: `${b.dur}s`, animationDelay: `${b.atraso}s` }} />
        ))}
      </div>

      {encerrando && (
        <div className="z-40 flex-none border-b border-amber-600/50 bg-amber-950/70 px-3 py-1.5 text-center text-[11px] font-bold text-amber-200"
          data-testid="gb-encerrando-aviso">
          O evento foi encerrado — termine a partida que já começou. Não é possível iniciar partidas novas.
        </div>
      )}

      {!encerrando && !!state?.pvp_locked && (
        <div className="z-40 flex-none border-b border-amber-600/50 bg-amber-950/70 px-3 py-1.5 text-center text-[11px] font-bold text-amber-200"
          data-testid="gb-manutencao-aviso">
          {state?.pvp_locked_message || 'Manutenção programada: as combinações e as fases voltam em breve.'}
          {' '}Quem já está jogando termina a partida e recebe o prêmio normal.
        </div>
      )}

      <header className="z-30 flex-none border-b border-purple-800/40 bg-[#07040d]/82 shadow-[0_6px_24px_rgba(0,0,0,.6)] backdrop-blur-xl"
        data-testid="gb-header-hud">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-x-2 gap-y-1.5 px-2 py-2">
          <button onClick={() => navigate('/conta')} data-testid="gb-back"
            className="flex-none rounded-full border border-purple-700/50 bg-[#150a24]/80 p-2 text-purple-200 transition-colors hover:border-orange-500 hover:text-orange-300 active:scale-95">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <GbAudioMenu />

          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-purple-800/40 bg-[#120722]/70 py-1 pl-1 pr-2.5 sm:flex-none" data-testid="gb-identity">
            {avatar && (
              <img src={avatar} alt="" data-testid="gb-avatar"
                className="h-10 w-10 flex-none rounded-full border-2 border-amber-500/80 object-cover shadow-[0_0_12px_rgba(245,158,11,.45)]" />
            )}
            <div className="min-w-0">
              <p className="truncate text-[13px] font-black leading-tight text-amber-100" data-testid="gb-nickname">
                {player?.nickname || 'JOGADOR'}
              </p>
              <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                <span className="flex flex-none items-center gap-1 rounded-full border border-amber-700/50 bg-amber-950/30 px-1.5 py-[1px]"
                  title={`${gbFmt(player?.xp)} XP · faltam ${gbFmt(player?.xp_to_next)} para o próximo nível`}
                  data-testid="gb-level">
                  <Star className="h-2.5 w-2.5 text-amber-300" />
                  <span className="font-mono text-[10px] font-black text-amber-200">Nv {player?.level || 1}</span>
                  <span className="h-1 w-8 overflow-hidden rounded-full bg-purple-950">
                    <span className="block h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500"
                      style={{ width: `${Math.min(100, Math.round(((player?.xp_into_level || 0) / Math.max(1, (player?.xp_next_level || 1) - (player?.xp_level_start || 0))) * 100))}%` }} />
                  </span>
                </span>
                {player?.clan ? (
                  <GbClanBadge clan={player.clan} size="sm" testid="gb-header-clan"
                    onClick={() => setTab('clas')} className="max-w-[140px]" />
                ) : (
                  <button onClick={() => setTab('clas')} data-testid="gb-header-noclan"
                    className="flex-none rounded-full border border-purple-800/70 px-2 py-[1px] text-[10px] font-black text-purple-300/70">
                    sem clã
                  </button>
                )}
                <span className="flex flex-none items-center gap-1 rounded-full border border-emerald-800/50 bg-emerald-950/30 px-1.5 py-[1px]"
                  title="vitórias no PvP" data-testid="gb-header-wins">
                  <Trophy className="h-2.5 w-2.5 text-emerald-300" />
                  <span className="font-mono text-[10px] font-black text-emerald-200">
                    {gbFmt(player?.stats?.pvp_wins || 0)}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className="flex w-full items-center gap-2 sm:w-auto sm:flex-none" data-testid="gb-balances">
            <button onClick={() => setTab('carteira')}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-amber-600/50 bg-gradient-to-r from-amber-950/70 to-black/60 px-3 py-1.5 shadow-[0_0_14px_rgba(245,158,11,.18)] transition-all hover:border-amber-400 active:scale-95 sm:flex-none"
              title="moedas amarelas do evento · toque para resgatar ou comprar"
              data-testid="gb-coin-balance">
              <GbCoin className="h-[1.1em]" />
              <span className="font-mono text-xs font-black text-amber-200 sm:text-sm">{gbFmt(player?.coins)}</span>
            </button>
            <button onClick={() => setTab('diamantes')}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-cyan-600/50 bg-gradient-to-r from-cyan-950/70 to-black/60 px-3 py-1.5 shadow-[0_0_14px_rgba(6,182,212,.18)] transition-all hover:border-cyan-400 active:scale-95 sm:flex-none"
              title="diamantes do evento · toque para ver o extrato"
              data-testid="gb-balance">
              <Gem className="h-3.5 w-3.5 flex-none text-cyan-300" />
              <span className="font-mono text-xs font-black text-cyan-200 sm:text-sm">{gbFmt(player?.diamonds)}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1500px] flex-1 overflow-y-auto overscroll-contain px-3 pt-3"
        data-testid="gb-scroll">
        {player?.blocked && (
          <div className="mb-4 flex items-center gap-2 rounded-2xl border border-rose-700/60 bg-rose-950/40 p-3 text-xs text-rose-200 backdrop-blur-sm"
            data-testid="gb-blocked-warning">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            Sua conta está bloqueada no evento. Fale com o suporte.
          </div>
        )}

        {/* brasão do evento: a capa do jogo */}
        <div className="gb-rise flex flex-col items-center pb-1 pt-1 sm:pt-2" data-testid="gb-logo-stage">
          <img src="/gb/halo_logo.webp" alt="GB Halloween · Fox Seller" data-testid="gb-event-logo"
            className="gb-logo-flutua w-[74vw] max-w-[300px] drop-shadow-[0_14px_34px_rgba(255,107,0,.45)] sm:max-w-[360px] xl:max-w-[420px]" />
          <p className="-mt-1 inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-gradient-to-r from-orange-950/85 via-black/80 to-cyan-950/80 px-3.5 py-1 text-[9px] font-black uppercase tracking-[0.22em] text-amber-200 backdrop-blur-md sm:text-[10px]"
            data-testid="gb-season-badge">
            <Flame className="h-3 w-3 text-orange-400" />
            temporada de halloween
            <span className="text-purple-600">·</span>
            arena pvp
            <Snowflake className="h-3 w-3 text-cyan-300" />
          </p>
          {state?.active && (
            <div data-testid="gb-countdown"
              className={`gb-rise mt-1.5 inline-flex items-center gap-2 rounded-full border px-3 py-1 backdrop-blur-md ${
                segundos < 86400
                  ? 'border-red-500/80 bg-gradient-to-r from-red-950/85 via-rose-950/80 to-red-950/85 shadow-[0_0_18px_rgba(255,40,60,.45)]'
                  : 'border-red-700/60 bg-gradient-to-r from-red-950/75 via-black/75 to-red-950/75 shadow-[0_0_14px_rgba(220,38,38,.28)]'}`}>
              <Hourglass className={`h-3.5 w-3.5 ${segundos < 86400 ? 'animate-pulse text-red-300' : 'text-red-400'}`} />
              <span className="text-[8px] font-black uppercase tracking-[0.2em] text-red-200/80 sm:text-[9px]">
                o evento acaba em
              </span>
              <span data-testid="gb-countdown-value"
                className={`text-xs font-black tabular-nums tracking-wider drop-shadow-[0_0_6px_rgba(255,60,60,.5)] sm:text-sm ${segundos < 86400 ? 'text-red-200' : 'text-red-300'}`}>
                {contagem(segundos)}
              </span>
            </div>
          )}
        </div>

        {/* sem animação/transform aqui: a batalha e os modais são `fixed` e um transform
            no pai os prenderia dentro do card (a partida travava na tela da capa) */}
        <div className="relative">
          <GbPvp player={player} clan={player?.clan} avatarUrl={avatar} config={config}
            travado={!!state?.pvp_locked} avisoTrava={state?.pvp_locked_message}
            onFinished={load} onWallet={() => setTab('carteira')} onFase={setPvpFase} />
          {/* paisagem/desktop: trilho flutuante na direita (também sai na sala) */}
          {!naSala && (
            <div className="absolute right-2 top-2 z-20 hidden flex-col gap-2 xl:flex" data-testid="gb-rail">
              {TABS.map(({ id, label, Icon }) => (
                <button key={id} onClick={() => setTab(id)} data-testid={`gb-rail-${id}`}
                  title={label}
                  className="group flex w-[56px] flex-col items-center gap-1 rounded-2xl border border-purple-700/50 bg-[#120722]/85 py-2.5 text-purple-200 shadow-lg shadow-black/50 backdrop-blur-xl transition-all hover:scale-105 hover:border-orange-500/80 hover:bg-[#1f0e38] hover:text-orange-300 active:scale-95">
                  <Icon className="h-5 w-5" />
                  <span className="text-[8px] font-black tracking-wide">{label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="h-24 xl:hidden" />
      </main>

      {/* celular em pé: barra fixa na parte de baixo — escondida na sala de combinação,
          lá o jogador só combina, sai ou duela (sem trocar de aba) */}
      {!naSala && (
        <nav className="fixed inset-x-0 bottom-0 z-[60] flex items-stretch justify-around gap-1 border-t border-purple-800/70 bg-[#07040d]/96 px-1.5 pb-[max(6px,env(safe-area-inset-bottom))] pt-1.5 shadow-[0_-8px_30px_rgba(0,0,0,.85)] backdrop-blur-2xl xl:hidden"
          data-testid="gb-dock">
          {TABS.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`gb-dock-${id}`}
              className={`flex flex-1 flex-col items-center gap-0.5 rounded-xl py-1.5 transition-all active:scale-95 ${
                tab === id
                  ? 'border-t-2 border-orange-400 bg-gradient-to-t from-orange-500/25 to-transparent text-orange-300'
                  : 'text-purple-300/70 hover:text-purple-100'}`}>
              <Icon className="h-5 w-5" />
              <span className="text-[8px] font-black tracking-wide">{label}</span>
            </button>
          ))}
        </nav>
      )}

      {tab !== 'inicio' && (
        <div className={`pwa-safe-top fixed inset-0 z-40 bg-[#0b0712] ${tab === 'missoes' ? 'overflow-hidden' : 'overflow-y-auto overscroll-contain'}`}
          data-testid="gb-panel">
          {tab !== 'missoes' && (
            <button onClick={() => setTab('inicio')} data-testid="gb-panel-close"
              className="pwa-safe-anchor fixed right-3 top-3 z-50 rounded-full border border-purple-700/60 bg-black/60 p-2.5 text-purple-100 backdrop-blur-sm">
              <X className="h-4 w-4" />
            </button>
          )}
          {tab === 'missoes' && (
            <GbWorldMap nodes={config?.nodes || []} progress={player?.progress} recem={recem}
              diamonds={Number(player?.diamonds || 0)} starting={starting} onPlay={play}
              travado={!!state?.pvp_locked} avisoTrava={state?.pvp_locked_message}
              onClose={() => setTab('inicio')} />
          )}
          <div className="mx-auto max-w-[1500px] px-3 pb-24 pt-3 xl:pb-6">
            {tab === 'loja' && <GbShop player={player} config={config} onChange={load} />}
            {tab === 'carteira' && <GbWallet config={config} onChange={load} />}
            {tab === 'diamantes' && <GbGems player={player} config={config} onChange={load} />}
            {tab === 'extrato' && <GbStatement scope="all" testid="gb-ledger" />}

            {tab === 'ranking' && <GbRanking meuId={player?.kako_id} />}
            {tab === 'clas' && <GbClans meuId={player?.kako_id} onChange={load} />}
          </div>
        </div>
      )}

      <GbNotices />

      {convite && (        <GbClanInvite convite={convite} entrando={entrandoCla}
          onAccept={aceitarConvite} onDecline={() => setConvite(null)} />
      )}

      {battle && (
        <GbBattle match={battle.match} node={battle.node} player={player} clan={player?.clan}
          avatarUrl={avatar} onFinished={onFinished} onItemUsed={() => load()}
          onClose={() => { setBattle(null); load(); }} />
      )}
    </div>
  );
}
